import PropTypes from "prop-types";
import { Button } from "primereact/button";
import CopilotMarkdown from "./CopilotMarkdown";
import CopilotInteractionForm from "./CopilotInteractionForm";
import { pick } from "./copilotUtils";
import { buildDailyReportExportAction } from "./copilotExport";

const CopilotCandidateCard = ({ block, onNavigate, compact = false }) => {
  const id = pick(block, "applicantId", "ApplicantId");
  const name = pick(block, "applicantName", "ApplicantName") || `#${id}`;
  const email = pick(block, "applicantEmail", "ApplicantEmail");
  const job = pick(block, "jobOpeningName", "JobOpeningName");
  const status = pick(block, "statusName", "StatusName");
  const missing = pick(block, "experienceSummary", "ExperienceSummary");
  const linkPath =
    pick(block, "linkPath", "LinkPath") ||
    (id ? `/applicant-details/${id}` : null);
  const severityRaw = String(pick(block, "title", "Title") || "").trim();
  const severity = /^(Critical|Medium|Low)$/i.test(severityRaw) ? severityRaw : "";
  const severityClass = severity.toLowerCase();

  return (
    <div
      className={`copilot-result-row${compact ? " copilot-result-row--compact" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => linkPath && onNavigate?.(linkPath)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && linkPath) onNavigate?.(linkPath);
      }}
    >
      <div className="copilot-result-row__icon copilot-result-row__icon--candidate">
        <i className="pi pi-user" />
      </div>
      <div className="copilot-result-row__body">
        <div className="copilot-result-row__title">
          {name}
          {severity ? (
            <span className={`copilot-result-row__chip copilot-result-row__chip--${severityClass}`}>
              {severity}
            </span>
          ) : null}
        </div>
        {email ? <div className="copilot-result-row__sub">{email}</div> : null}
        {(job || status) ? (
          <div className="copilot-result-row__meta">
            {[job, status].filter(Boolean).join(" · ")}
          </div>
        ) : null}
        {missing ? <div className="copilot-result-row__sub">{missing}</div> : null}
      </div>
      {linkPath ? (
        <button
          type="button"
          className="copilot-result-row__link"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate?.(linkPath);
          }}
        >
          Open
        </button>
      ) : null}
    </div>
  );
};

CopilotCandidateCard.propTypes = {
  block: PropTypes.object.isRequired,
  onNavigate: PropTypes.func,
  compact: PropTypes.bool,
};

const CopilotJobOpeningCard = ({ block, onNavigate, onAction, compact = false }) => {
  const id = pick(block, "jobOpeningId", "JobOpeningId");
  const name = pick(block, "jobOpeningName", "JobOpeningName") || "Opening";
  const daysOpen = pick(block, "daysOpen", "DaysOpen");
  const match = pick(block, "matchPercent", "MatchPercent");
  const editPath = id ? `/job-opening?editId=${id}` : "/job-opening";
  const emailPrompt = id
    ? `Email shortlist for ${name} (opening #${id})`
    : null;

  return (
    <div
      className={`copilot-result-row${compact ? " copilot-result-row--compact" : ""}`}
      role={id ? "button" : undefined}
      tabIndex={id ? 0 : undefined}
      onClick={() => id && onNavigate?.(editPath)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && id) onNavigate?.(editPath);
      }}
    >
      <div className="copilot-result-row__icon copilot-result-row__icon--job">
        <i className="pi pi-briefcase" />
      </div>
      <div className="copilot-result-row__body">
        <div className="copilot-result-row__title">{name}</div>
        <div className="copilot-result-row__meta">
          {daysOpen != null ? `${daysOpen}d open` : null}
          {match != null ? (
            <span className="copilot-result-row__badge">{match}% match</span>
          ) : null}
        </div>
      </div>
      <div className="copilot-result-row__actions">
        {emailPrompt ? (
          <button
            type="button"
            className="copilot-result-row__link"
            onClick={(e) => {
              e.stopPropagation();
              onAction?.({
                type: "prompt",
                label: `Email shortlist for ${name}`,
                payload: { prompt: emailPrompt },
              });
            }}
          >
            Email
          </button>
        ) : null}
        <button
          type="button"
          className="copilot-result-row__link"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate?.(editPath);
          }}
        >
          View
        </button>
      </div>
    </div>
  );
};

CopilotJobOpeningCard.propTypes = {
  block: PropTypes.object.isRequired,
  onNavigate: PropTypes.func,
  onAction: PropTypes.func,
  compact: PropTypes.bool,
};

const CopilotInterviewerCard = ({ block, onAction, compact = false }) => {
  const id = pick(block, "applicantId", "ApplicantId");
  const name = pick(block, "applicantName", "ApplicantName") || `#${id}`;
  const email = pick(block, "applicantEmail", "ApplicantEmail");
  const meta = pick(block, "statusName", "StatusName");
  const prompt = id ? `select interviewer ${id}` : null;
  const viewSlots = () => {
    if (!prompt) return;
    onAction?.({
      type: "prompt",
      label: `View slots for ${name}`,
      payload: { prompt },
    });
  };

  return (
    <div
      className={`copilot-result-row${compact ? " copilot-result-row--compact" : ""}`}
      role={prompt ? "button" : undefined}
      tabIndex={prompt ? 0 : undefined}
      onClick={viewSlots}
      onKeyDown={(e) => {
        if (e.key === "Enter") viewSlots();
      }}
    >
      <div className="copilot-result-row__icon copilot-result-row__icon--interviewer">
        <i className="pi pi-users" />
      </div>
      <div className="copilot-result-row__body">
        <div className="copilot-result-row__title">{name}</div>
        {email ? <div className="copilot-result-row__sub">{email}</div> : null}
        {meta ? <div className="copilot-result-row__meta">{meta}</div> : null}
      </div>
      {prompt ? (
        <button
          type="button"
          className="copilot-result-row__link"
          onClick={(e) => {
            e.stopPropagation();
            viewSlots();
          }}
        >
          View slots
        </button>
      ) : null}
    </div>
  );
};

CopilotInterviewerCard.propTypes = {
  block: PropTypes.object.isRequired,
  onAction: PropTypes.func,
  compact: PropTypes.bool,
};

const CopilotSlotCard = ({ block, compact = false }) => {
  const name = pick(block, "applicantName", "ApplicantName");
  const title = pick(block, "title", "Title") || pick(block, "statusName", "StatusName") || "Slot";

  return (
    <div className={`copilot-result-row${compact ? " copilot-result-row--compact" : ""}`}>
      <div className="copilot-result-row__icon copilot-result-row__icon--slot">
        <i className="pi pi-calendar" />
      </div>
      <div className="copilot-result-row__body">
        <div className="copilot-result-row__title">{title}</div>
        {name ? <div className="copilot-result-row__sub">{name}</div> : null}
      </div>
    </div>
  );
};

CopilotSlotCard.propTypes = {
  block: PropTypes.object.isRequired,
  compact: PropTypes.bool,
};

const CopilotResumePreviewCard = ({ block }) => {
  const name = pick(block, "applicantName", "ApplicantName");
  const email = pick(block, "applicantEmail", "ApplicantEmail");
  const phone = pick(block, "phone", "Phone");
  const skills = pick(block, "skillsSummary", "SkillsSummary");
  const experience = pick(block, "experienceSummary", "ExperienceSummary");
  const processed = pick(block, "isProcessed", "IsProcessed");
  const fileName = pick(block, "title", "Title");

  return (
    <div className="copilot-block-card copilot-block-card--resume">
      <div className="copilot-block-card__head">
        <i className="pi pi-file" />
        <span>{fileName || name || "Resume preview"}</span>
        <span className={`copilot-status-pill${processed ? " is-ready" : ""}`}>
          {processed ? "Parsed" : "Pending"}
        </span>
      </div>
      {name ? <div className="copilot-block-card__line"><strong>{name}</strong></div> : null}
      {email ? <div className="copilot-block-card__line">{email}</div> : null}
      {phone ? <div className="copilot-block-card__line">{phone}</div> : null}
      {skills ? <div className="copilot-block-card__line copilot-block-card__line--muted">{skills}</div> : null}
      {experience ? <div className="copilot-block-card__line copilot-block-card__line--muted">{experience}</div> : null}
    </div>
  );
};

CopilotResumePreviewCard.propTypes = {
  block: PropTypes.object.isRequired,
};

const CopilotMetricSummary = ({ block, onAction }) => {
  const metrics = pick(block, "metrics", "Metrics") || [];
  const handleExport = () => {
    onAction?.(buildDailyReportExportAction(new Date()));
  };
  return (
    <div className="copilot-block-card">
      <div className="copilot-block-card__head copilot-block-card__head--split">
        <div className="copilot-block-card__head-main">
          <i className="pi pi-chart-bar" />
          <span>Recruitment metrics</span>
        </div>
        {onAction ? (
          <Button
            type="button"
            label="Export"
            icon="pi pi-download"
            className="p-button-text p-button-sm copilot-metric-export"
            onClick={handleExport}
          />
        ) : null}
      </div>
      <div className="copilot-metrics-grid">
        {metrics.map((m) => (
          <div key={pick(m, "label", "Label")} className="copilot-metric-item">
            <span className="copilot-metric-item__value">
              {pick(m, "value", "Value")}
            </span>
            <span className="copilot-metric-item__label">
              {pick(m, "label", "Label")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

CopilotMetricSummary.propTypes = {
  block: PropTypes.object.isRequired,
  onAction: PropTypes.func,
};

const CopilotConfirmationCard = ({ block, onAction }) => {
  const title = pick(block, "title", "Title");
  const markdown = pick(block, "markdown", "Markdown");
  const applicant = pick(block, "applicantName", "ApplicantName");
  const status = pick(block, "statusName", "StatusName");
  const opening = pick(block, "jobOpeningName", "JobOpeningName");
  const fields = [applicant && { label: "Candidate", value: applicant }, status && { label: "Status", value: status }, opening && { label: "Opening", value: opening }].filter(Boolean);

  return (
    <div className="copilot-block-card copilot-block-card--confirm">
      <div className="copilot-block-card__head">
        <i className="pi pi-exclamation-circle" />
        <span>{title || "Action preview"}</span>
      </div>
      {markdown ? (
        <div className="copilot-block-card__preview">
          <CopilotMarkdown text={markdown} />
        </div>
      ) : null}
      {fields.length ? (
        <dl className="copilot-confirm-fields">
          {fields.map((f) => (
            <div key={f.label} className="copilot-confirm-fields__row">
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <p className="copilot-block-card__hint">
        Confirm to continue, or cancel. You can also type <strong>yes</strong>.
      </p>
      <div className="copilot-confirm-actions">
        <Button
          type="button"
          label="Confirm"
          icon="pi pi-check"
          className="p-button-sm"
          onClick={() => onAction?.({ type: "prompt", payload: { prompt: "yes" } })}
        />
        <Button
          type="button"
          label="Cancel"
          icon="pi pi-times"
          className="p-button-sm p-button-outlined p-button-secondary"
          onClick={() => onAction?.({ type: "prompt", payload: { prompt: "cancel" } })}
        />
      </div>
    </div>
  );
};

CopilotConfirmationCard.propTypes = {
  block: PropTypes.object.isRequired,
  onAction: PropTypes.func,
};

export const CopilotBlockRenderer = ({
  block,
  onNavigate,
  onAction,
  compact = false,
  workflow = null,
  formDisabled = false,
  onFormSave,
  onFormCancel,
  onFormRefresh,
}) => {
  const type = (pick(block, "type", "Type") || "text").toLowerCase();

  switch (type) {
    case "candidatecard":
      return (
        <CopilotCandidateCard block={block} onNavigate={onNavigate} compact={compact} />
      );
    case "jobopeningcard":
      return (
        <CopilotJobOpeningCard
          block={block}
          onNavigate={onNavigate}
          onAction={onAction}
          compact={compact}
        />
      );
    case "interviewercard":
      return (
        <CopilotInterviewerCard block={block} onAction={onAction} compact={compact} />
      );
    case "slotcard":
      return <CopilotSlotCard block={block} compact={compact} />;
    case "resumepreview":
      return <CopilotResumePreviewCard block={block} />;
    case "metricsummary":
      return <CopilotMetricSummary block={block} onAction={onAction} />;
    case "confirmation":
      return <CopilotConfirmationCard block={block} onAction={onAction} />;
    case "interactionform":
      return (
        <CopilotInteractionForm
          block={block}
          workflow={workflow}
          disabled={formDisabled}
          onSave={onFormSave}
          onCancel={onFormCancel}
          onRefresh={onFormRefresh}
        />
      );
    case "text":
    default:
      return (
        <div className="copilot-block-text">
          <CopilotMarkdown
            text={pick(block, "markdown", "Markdown") || ""}
            onBulletClick={onAction ? (item) => onAction({ type: "prompt", payload: { prompt: item } }) : undefined}
          />
        </div>
      );
  }
};

CopilotBlockRenderer.propTypes = {
  block: PropTypes.object.isRequired,
  onNavigate: PropTypes.func,
  onAction: PropTypes.func,
  compact: PropTypes.bool,
  workflow: PropTypes.object,
  formDisabled: PropTypes.bool,
  onFormSave: PropTypes.func,
  onFormCancel: PropTypes.func,
  onFormRefresh: PropTypes.func,
};

export default CopilotBlockRenderer;
